import Vue from "vue";
import config from "@/config";
import uuid from "uuid/v4";
import Vuex from "vuex";
import { createDirectStore } from "direct-vuex";

import template from "./template/index";
import batch from "./batch/index";
import session from "./session/index";
import request from "./request/index";
import form from "./form/index";
import user from "./user/index";
import { DataOptions } from "vuetify";

Vue.use(Vuex);

export type Message = {
  id?: string;
  message: string;
  type: string;
};

export type Pagination = {
  page: number;
  itemsPerPage: number;
  sortDesc: boolean;
  sortBy?: string;
};

export type Filter = { type: string; value: string | number | string[] };

export type RootState = {
  loading: number;
  messages: Message[];
  pagination: Pagination;
  totalItems: number;
  filters: Filter[];
};

const { store, rootActionContext, moduleActionContext } = createDirectStore({
  state: (): RootState => {
    return {
      loading: 0,
      messages: [],
      pagination: {
        page: 1,
        itemsPerPage: 10,
        sortDesc: false
      },
      totalItems: 0,
      filters: []
    };
  },
  getters: {
    options(state): DataOptions {
      return {
        page: state.pagination.page,
        itemsPerPage: state.pagination.itemsPerPage,
        sortBy: state.pagination.sortBy ? [state.pagination.sortBy] : [],
        sortDesc: [state.pagination.sortDesc],
        mustSort: false,
        multiSort: false,
        groupBy: [],
        groupDesc: []
      };
    }
  },
  mutations: {
    START_LOADING(state) {
      state.loading++;
    },
    END_LOADING(state) {
      state.loading--;
    },
    PUSH_MESSAGE(state, attrs: { message: string; type: string }) {
      let id = uuid();
      state.messages.push({ ...attrs, id: id });
      return id;
    },
    REMOVE_MESSAGE(state, id: string) {
      let idx = state.messages.findIndex(message => {
        return message.id == id;
      });
      if (idx > 0) {
        state.messages.splice(idx, 1);
      }
    },
    REMOVE_LAST_MESSAGE(state) {
      state.messages.splice(0, 1);
    },
    SET_PAGINATION(state, pagination: Pagination) {
      state.pagination = { ...pagination };
    },
    SET_PAGINATION_FROM_DATA_OPTIONS(state, options: DataOptions) {
      state.pagination = {
        page: options.page,
        itemsPerPage: options.itemsPerPage,
        sortDesc: options.sortDesc.length > 0 ? options.sortDesc[0] : false,
        sortBy: options.sortBy.length > 0 ? options.sortBy[0] : undefined
      };
    },
    SET_TOTAL_ITEMS(state, val: number) {
      state.totalItems = val;
    },
    CLEAR_PAGINATION(state) {
      state.pagination = {
        page: 1,
        itemsPerPage: 10,
        sortDesc: false
      };
    },
    ADD_FILTER(state, filter: Filter) {
      let idx = state.filters.findIndex(item => {
        return item.type == filter.type;
      });
      if (idx >= 0) {
        state.filters.splice(idx, 1, filter);
      } else {
        state.filters.push(filter);
      }
    },
    REMOVE_FILTER(state, filter: Filter) {
      let idx = state.filters.findIndex(item => {
        return item.type == filter.type;
      });
      if (idx >= 0) {
        state.filters.splice(idx, 1);
      }
    },
    CLEAR_FILTERS(state) {
      state.filters = [];
    }
  },
  modules: {
    template,
    batch,
    session,
    request,
    form,
    user
  }
});

export default store;

export { rootActionContext, moduleActionContext };

export type AppStore = typeof store;

declare module "vuex" {
  interface Store<S> {
    direct: AppStore;
  }
}
